import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export const useScrollAnimations = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const ctx = gsap.context(() => {
      // Fade in animations for sections
      gsap.utils.toArray('.scroll-section').forEach((section: any) => {
        gsap.fromTo(section, 
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1.5,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 85%',
              end: 'top 15%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      });

      // Stagger animations for lists
      gsap.utils.toArray('.scroll-list').forEach((list: any) => {
        gsap.fromTo(list.children,
          { opacity: 0, x: -30 },
          {
            opacity: 1,
            x: 0,
            duration: 0.8,
            stagger: 0.2,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: list,
              start: 'top 90%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      });

      // Scale animations for cards
      gsap.utils.toArray('.scroll-card').forEach((card: any) => {
        gsap.fromTo(card,
          { opacity: 0, scale: 0.9 },
          {
            opacity: 1,
            scale: 1,
            duration: 1.0,
            ease: 'elastic.out(1, 0.5)',
            scrollTrigger: {
              trigger: card,
              start: 'top 95%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      });

      // Parallax effect for hero
      const hero = document.querySelector('.hero-section');
      if (hero) {
        gsap.to(hero, {
          yPercent: 30,
          ease: 'none',
          scrollTrigger: {
            trigger: hero,
            start: 'top top',
            end: 'bottom top',
            scrub: true
          }
        });
      }

    }, containerRef);

    return () => ctx.revert();
  }, []);

  return containerRef;
};

export const ScrollSection = ({ children, className = '' }: { children: React.ReactNode, className?: string }) => (
  <div className={`scroll-section ${className}`}>
    {children}
  </div>
);

export const ScrollList = ({ children, className = '' }: { children: React.ReactNode, className?: string }) => (
  <div className={`scroll-list ${className}`}>
    {children}
  </div>
);

export const ScrollCard = ({ children, className = '' }: { children: React.ReactNode, className?: string }) => (
  <div className={`scroll-card ${className}`}>
    {children}
  </div>
);
